<template>
  <PagePopup :model-value="model" @update:model-value="onOpenChange">
    <DomainContext :domain="pageDomain">
      <PagePopupContent :background="background">
        <template #header>
          <slot name="header">
            <div v-if="props.urls.length > 1" class="flex gap-2">
              <Button
                :variant="headerButtonVariant"
                size="square"
                :disabled="currentPageIndex === 0"
                aria-label="Vorige pagina"
                @click="previousPage">
                <ChevronLeftIcon class="w-3 h-3" />
              </Button>
              <Button
                :variant="headerButtonVariant"
                aria-label="Volgende pagina"
                size="square"
                :disabled="currentPageIndex === props.urls.length - 1"
                @click="nextPage">
                <ChevronRightIcon class="w-3 h-3" />
              </Button>
            </div>
          </slot>
          <DialogTitle class="sr-only">Pagina popup</DialogTitle>
        </template>

        <Transition
          mode="out-in"
          enter-active-class="transition-all duration-300 ease-smooth"
          leave-active-class="transition-all duration-300 ease-smooth"
          :enter-from-class="direction === 'forward' ? 'translate-x-10 opacity-0' : '-translate-x-10 opacity-0'"
          enter-to-class="translate-x-0 opacity-100"
          leave-from-class="translate-x-0 opacity-100"
          :leave-to-class="direction === 'forward' ? '-translate-x-10 opacity-0' : 'translate-x-10 opacity-0'">
          <div :key="currentPageIndex" class="h-full w-full">
            <div v-if="isError || isLoading" class="h-full w-full flex items-center justify-center">
              <div v-if="isError" class="flex flex-col gap-4 items-center">
                <Typography as="p" variant="body" class="text-theme-100">
                  Er is iets misgegaan. Probeer het opnieuw.
                </Typography>
                <Button variant="theme" @click="_fetchPage">Probeer opnieuw</Button>
              </div>
              <Spinner v-else class="w-7 h-7 text-theme-100" />
            </div>
            <div class="md:pt-10 pb-24 md:pb-50">
              <slot v-if="!isLoading && page" :page="page" />
            </div>
          </div>
        </Transition>
      </PagePopupContent>
    </DomainContext>
  </PagePopup>
</template>

<script setup lang="ts" generic="TPage = unknown">
import type { Domain } from '@gro-up/types'
import { useAsyncPagePopupHistory } from '@gro-up/ui/composables'
import { ChevronLeftIcon, ChevronRightIcon } from '@gro-up/ui/icons'
import { PagePopup, PagePopupContent, Button, Spinner, Typography } from '@gro-up/ui/primitives'
import { DialogTitle } from 'radix-vue'
import { watch, ref, inject, provide, computed, type Ref, useId, onBeforeUnmount } from 'vue'

import DomainContext from '../DomainContext/DomainContext.vue'

type AsyncPagePopupProps = {
  urls: string[]
  initialUrlIndex?: number
  domain: Domain | null
  /**
   * App-specific fetcher that resolves the page content for a given url.
   * Keeps this component decoupled from any app's API layer / page model.
   */
  fetchPage: (url: string) => Promise<TPage>
  /**
   * Popup shell styling: 'theme' follows the active domain, 'neutral' is the fixed beige
   * shell used on corporate. The header controls follow the same choice.
   */
  background?: 'theme' | 'neutral'
}

const props = withDefaults(defineProps<AsyncPagePopupProps>(), {
  initialUrlIndex: 0,
  background: 'theme',
})

// On the neutral shell the themed variant clashes with the beige, so match the close button.
const headerButtonVariant = computed(() => (props.background === 'neutral' ? 'primary' : 'theme-inverse'))

const model = defineModel<boolean>('modelValue')
const id = useId()
const openAsyncPagePopupIds = inject<Ref<string[]>>('openAsyncPagePopupIds')

const { showUrl, popUrl, restoreUrl } = useAsyncPagePopupHistory({
  isOpen: () => !!model.value,
  onNavigateAway: () => {
    model.value = false
  },
})

// Closing from the popup itself (close button, escape, overlay click) leaves the visitor on
// the page underneath, so pop the entry the popup pushed.
const onOpenChange = (isOpen?: boolean) => {
  if (!isOpen) {
    popUrl()
  }

  model.value = isOpen
}

// Content inside the popup usually navigates elsewhere as it closes (see CustomNuxtLink),
// so rewrite the pushed entry here instead of popping it.
provide('closeAsyncPagePopup', () => {
  restoreUrl()
  model.value = false
})

const page = ref<TPage | null>(null) as Ref<TPage | null>
const currentPageIndex = ref(props.initialUrlIndex)
const isLoading = ref(false)
const isError = ref(false)
const direction = ref<'forward' | 'backward' | null>(null)

const pageDomain = computed<Domain | null>(
  () => (page.value as { domain?: Domain | null } | null)?.domain ?? props.domain,
)

const _fetchPage = async () => {
  const url = props.urls[currentPageIndex.value]
  if (!url) {
    return
  }

  showUrl(url)

  try {
    isLoading.value = true
    isError.value = false
    page.value = await props.fetchPage(url)
  } catch (e) {
    console.error('AsyncPagePopup error:', e)
    isError.value = true
  } finally {
    isLoading.value = false
  }
}

const previousPage = () => {
  if (currentPageIndex.value !== null && currentPageIndex.value > 0) {
    direction.value = 'backward'
    currentPageIndex.value--
  }
}

const nextPage = () => {
  if (currentPageIndex.value !== null && currentPageIndex.value < props.urls.length - 1) {
    direction.value = 'forward'
    currentPageIndex.value++
  }
}

onBeforeUnmount(() => {
  openAsyncPagePopupIds?.value.splice(openAsyncPagePopupIds?.value.indexOf(id), 1)
})

watch(
  currentPageIndex,
  () => {
    if (model.value) {
      _fetchPage()
    }
  },
  { immediate: true },
)

watch(model, () => {
  if (model.value) {
    page.value = null
    currentPageIndex.value = props.initialUrlIndex
    openAsyncPagePopupIds?.value.push(id)
    _fetchPage()
  } else {
    // No-op when the close already handled the history entry; covers a parent closing the
    // popup programmatically.
    restoreUrl()
    openAsyncPagePopupIds?.value.splice(openAsyncPagePopupIds?.value.indexOf(id), 1)
  }
})
</script>
